<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SecondaryMenu from './components/SecondaryMenu.vue'
import { useThemeStore } from './stores/themeStore'
import { useUserStore } from './stores/userStore'
import NotificationToast from './components/NotificationToast.vue'
import SessionExpiredModal from './components/SessionExpiredModal.vue'
import { useNotificationStore } from './stores/notificationStore'
import NotificationOverlay from './components/NotificationOverlay.vue'
import ChatButton from './components/ChatButton.vue'
import TutorialOverlay from './components/TutorialOverlay.vue'
import UsersMenuPopup from './components/UsersMenuPopup.vue'
import { useMenuStore } from './stores/menuStore'
import FixturesPage from './views/FixturesPage.vue'
import TicketsMenuPopup from './components/TicketsMenuPopup.vue'
import ProfileTicketPopup from './components/ProfileTicketPopup.vue'
import WorkoutDiaryOverlay from './components/WorkoutDiaryOverlay.vue'
import ServerManagerOverlay from './components/ServerManagerOverlay.vue'
import PlayerDetailsPage from './views/PlayerDetailsPage.vue'
import TemplatesMenuPopup from './components/TemplatesMenuPopup.vue'
import SidebarNotificationsPanel from './components/SidebarNotificationsPanel.vue'
import ThirdMenu from './components/ThirdMenu.vue'
import { useSplitViewStore } from './stores/splitViewStore'
import DatabasePopupWindows from './components/DatabasePopupWindows.vue'
import DatabaseAuthModal from './components/DatabaseAuthModal.vue'
import { useDatabasePopupStore } from './stores/databasePopupStore'
import AuthDebugger from './components/AuthDebugger.vue'
import axios from './axios.js'
import GlobalSearchPopup from './components/global-seach/GlobalSearchPopup.vue'
import GoToFixturePopup from './components/global-seach/GoToFixturePopup.vue'
import AutomatedProjectionsProfiles from './components/AutomatedProjectionsProfiles.vue'
import AutoV2 from './components/AutoV2.vue'
import { useProjectionsStore } from './stores/projections'
import { useCurrentUserPermissions } from './composables/usePermissions'
import { usePageTitle } from './composables/usePageTitle'
import ApiManagerPopup from './components/ApiManagerPopup.vue'
import PlayerAliasManager from './components/PlayerAliasManager.vue'
import BoxscoresOverlay from './components/boxscores/BoxscoresOverlay.vue'
import SSETestPanel from './components/SSETestPanel.vue'
import GlobalContextMenu from './components/GlobalContextMenu.vue'
import BalancedLinesLadderPanel from './components/BalancedLinesLadderPanel.vue'
import TemplateManagementPopup from './components/TemplateManagementPopup.vue'
//import ErrorOverlay from './components/ErrorOverlay.vue'
import GamesNotificationHeader from './components/GamesNotificationHeader.vue'
import GamesNotificationOverlay from './components/GamesNotificationOverlay.vue'
import GlobalFixtureCalendarModal from './components/GlobalFixtureCalendarModal.vue'
import { useGamesNotificationStore } from './stores/gamesNotificationStore'

const { hasAnyPermission } = useCurrentUserPermissions()

const gamesNotificationStore = useGamesNotificationStore()
const hasNewGames = computed(() => gamesNotificationStore.hasNewGames)

const projectionsStore = useProjectionsStore()

// Initialize dynamic page title
const { updateTitle, resetTitle } = usePageTitle()



const route = useRoute()
const router = useRouter()
const isSidebarCollapsed = ref(true)
const isSecondaryMenuOpen = ref(false)
const isGamesMenuOpen = ref(false)
const showWorkoutDiary = ref(false)
const isServerManagerOpen = ref(false)
const showServerManager = ref(false)
import WebSocketStatus from './components/WebSocketStatus.vue'
import WebSocketUpdatesPanel from './components/WebSocketUpdatesPanel.vue'

const allMenuItems = [
  {
    id: 'games',
    label: 'Sports',
    icon: 'fa-basketball-ball',
    isOpen: true,
    path: '/games',
    hasSubmenu: true,
    requiredPermissions: ['view_fixtures'], // Clients, Traders, Admins can see this
    subItems: [
      { 
        id: 'fixtures', 
        label: 'Basketball', 
        icon: 'fa-calendar-alt', 
        path: '/games/fixtures',
        requiredPermissions: ['view_fixtures']
      },
      { 
        id: 'rosters', 
        label: 'Football', 
        icon: 'fa-users', 
        path: '/games/rosters',
        requiredPermissions: ['view_fixtures']
      },
      { 
        id: 'american-football', 
        label: 'American Football', 
        icon: 'fa-football-ball', 
        path: '/games/american-football',
        requiredPermissions: ['view_fixtures']
      },
      { 
        id: 'players', 
        label: 'Tennis', 
        icon: 'fa-user', 
        path: '/games/players',
        requiredPermissions: ['view_fixtures']
      },
    ]
  },
  /*{
    id: 'templates',
    label: 'Templates',
    icon: 'fa-file-alt',
    hasSubmenu: false,
    requiredPermissions: ['edit_odds', '*'], // Only Traders and Admins
    onClick: () => showTemplatesMenu.value = true
  },*/
  /*{
    id: 'template-management',
    label: 'Template Management',
    icon: 'fa-cogs',
    hasSubmenu: false,
    requiredPermissions: ['edit_odds', '*', 'view_fixtures', 'view_odds', 'manage_templates'], // Admins, Traders, and Clients (any authenticated user)
    onClick: () => showBalancedLinesLadder.value = true
  },*/
  {
    id: 'users',
    label: 'Users',
    icon: 'fa-users',
    hasSubmenu: false,
    requiredPermissions: ['*'], // Only Admins
    onClick: () => showUsersMenu.value = true
  },
  /*{
    id: 'tickets',
    label: 'Tickets',
    icon: 'fa-ticket-alt',
    hasSubmenu: false,
    badge: 3,
    requiredPermissions: ['view_tickets'], // All authenticated users
    onClick: () => showTicketsMenu.value = true
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: 'fa-chart-bar',
    path: '/monitoring',
    requiredPermissions: ['manage_risk', '*'] // Traders and Admins
  },
  {
    id: 'server-manager',
    label: 'Server Manager',
    icon: 'fa-server',
    hasSubmenu: false,
    requiredPermissions: ['*'], // Only Admins
    onClick: () => {
      showServerManager.value = true
    }
  },
  */{
    // Client accounts only: admins/traders reach the Messageboard from the global right-click menu.
    id: 'messageboard',
    label: 'Messageboard',
    icon: 'fa-stream',
    path: '/messageboard',
    clientOnly: true,
    requiredPermissions: ['view_fixtures']
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'fa-cog',
    path: '/settings',
    requiredPermissions: ['view_fixtures'] // All authenticated users
  },
  {
    id: 'players-alias-management',
    label: 'Player Aliases',
    icon: 'fa-id-badge',
    hasSubmenu: false,
    requiredPermissions: ['admin.access', '*'], // Only Admins
    onClick: () => showPlayersAliasManager.value = true
  },

{
  id: 'boxscores',
  label: 'Boxscores',
  icon: 'fa-table',
  hasSubmenu: false,
  requiredPermissions: ['manage_boxscores', '*'], // Admins and users with boxscore management
  onClick: () => showBoxscoresOverlay.value = true
},
{
  id: 'balanced-lines-ladder',
  label: 'Balanced Lines Ladder',
  icon: 'fa-list-ol',
  hasSubmenu: false,
  requiredPermissions: ['edit_odds', '*'], // Traders and Admins
  onClick: () => showBalancedLinesLadder.value = true
},
]

// Helper function to check if user has required permissions
const hasPermission = (requiredPermissions, userPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true
  if (!userPermissions || userPermissions.length === 0) return false
  
  // If user has '*' permission (admin), allow everything
  if (userPermissions.includes('*')) return true
  
  // Check if user has any of the required permissions
  return requiredPermissions.some(permission => 
    permission === '*' ? userPermissions.includes('*') : userPermissions.includes(permission)
  )
}

// Get user permissions - now uses individual user permissions instead of role-based
const getUserPermissions = computed(() => {
  const user = userStore.currentUser
  if (!user) return []
  
  // Use individual user permissions if available, otherwise fall back to role-based defaults
  return user.permissions || userStore.getDefaultPermissionsForRole(user.role)
})

// Filter menu items based on user permissions
const menuItems = computed(() => {
  // If no user is logged in, return minimal menu (just settings)
  if (!userStore.isAuthenticated || !userStore.currentUser) {
    return allMenuItems.filter(item => item.id === 'settings')
  }
  
  const userPermissions = getUserPermissions.value
  
  return allMenuItems.filter(item => {
    // Special case: Template Management is available to all authenticated users (clients, traders, admins)
    if (item.id === 'template-management') {
      return true // All authenticated users can access template management
    }
    
    // Client-only entries (e.g. Messageboard) never show for traders/admins
    if (item.clientOnly && userStore.currentUser?.role !== 'client') {
      return false
    }

    // Check if user has permission for main menu item
    if (!hasPermission(item.requiredPermissions, userPermissions)) {
      return false
    }
    
    // If item has submenu, filter sub-items as well
    if (item.hasSubmenu && item.subItems) {
      // Create a copy of subItems to avoid mutating the original
      const filteredSubItems = item.subItems.filter(subItem => 
        hasPermission(subItem.requiredPermissions, userPermissions)
      )
      
      // Create a new item object with filtered subItems
      const filteredItem = { ...item, subItems: filteredSubItems }
      
      // Hide main menu item if no sub-items are visible
      if (filteredSubItems.length === 0) {
        return false
      }
      
      // Replace the current item in the array
      Object.assign(item, filteredItem)
    }
    
    return true
  })
})

const isActive = (path) => {
  return route.path.startsWith(path)
}

// Watch for route changes to handle league routes
watch(
  () => route.path,
  (newPath) => {
    // Check if we're on a league route
    if (newPath.includes('/games/fixtures/league/')) {
      isSidebarCollapsed.value = true
      isSecondaryMenuOpen.value = true
      isGamesMenuOpen.value = true
    }
    
    // Reset title when navigating away from fixture pages
    if (!/\/fixture(?:-v\d+)?\//.test(newPath)) {
      resetTitle()
    }
  },
  { immediate: true } // Run on component mount
)

const toggleSubmenu = (menuItem) => {
  if (menuItem.id === 'games') {
    isGamesMenuOpen.value = !isGamesMenuOpen.value
    if (menuItem.hasSubmenu) {
      isSecondaryMenuOpen.value = isGamesMenuOpen.value
      if (isGamesMenuOpen.value && !route.path.startsWith('/games')) {
        router.push('/games/fixtures')
      }
    }
  }
}

const showSecondaryMenu = computed(() => {
  return route.path.startsWith('/games') && isSecondaryMenuOpen.value
})

const themeStore = useThemeStore()

const toggleTheme = () => {
  const currentTheme = themeStore.theme
  if (currentTheme === 'light') {
    themeStore.setTheme('dark')
  } else if (currentTheme === 'dark') {
    themeStore.setTheme('system')
  } else {
    themeStore.setTheme('light')
  }
}

const getThemeIcon = computed(() => {
  switch (themeStore.theme) {
    case 'light':
      return 'fa-sun'
    case 'dark':
      return 'fa-moon'
    default:
      return 'fa-desktop'
  }
})

const userStore = useUserStore()
const showProfileMenu = ref(false)
// Ctrl/Cmd + G "go to fixture by ID" popup
const showGoToFixture = ref(false)

const currentUser = computed(() => userStore.currentUser || {
  name: 'Guest',
  email: '',
  avatar: null,
  role: '',
  status: 'offline'
})

const userInitials = computed(() => {
  const names = currentUser.value.name.split(' ')
  return names.map(n => n[0]).join('').toUpperCase()
})

const logout = async () => {
  try {
    await userStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
  }
}

const notificationStore = useNotificationStore()
const showNotifications = ref(false)
const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notificationStore.unreadCount)
const sseConnected = computed(() => notificationStore.isSSEConnected)

const markAsRead = (notification) => {
  notificationStore.markAsRead(notification.id)
}

const markAllAsRead = () => {
  notificationStore.markAllAsRead()
}

const showTutorials = ref(false)

const handleTutorialClick = (menuItem) => {
  if (menuItem.hasSubmenu) {
    showTutorials.value = true
  }
}

const showUsersMenu = ref(false)

const menuStore = useMenuStore()
const showThirdMenu = computed(() => menuStore.showThirdMenu)
const thirdMenuContent = computed(() => menuStore.thirdMenuContent)

watch(
  () => menuStore.openGamesSidebar,
  (shouldOpen) => {
    if (!shouldOpen) return
    isSecondaryMenuOpen.value = true
    isGamesMenuOpen.value = true
    if (!route.path.startsWith('/games')) {
      router.push('/games/fixtures')
    }
  }
)

// Add this function to handle ESC key
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && menuStore.showThirdMenu) {
    menuStore.closeThirdMenu()
  }
  // Ctrl/Cmd + K to open global search
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault()
    projectionsStore.globalSearchPopup = true
  }
  // Ctrl/Cmd + Shift + F to open global search (alternative shortcut)
  if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'F' && userStore.currentUser.role === 'admin') {
    event.preventDefault()
    projectionsStore.globalSearchPopup = true
  }
  // Ctrl/Cmd + G to open the "go to fixture by ID" popup
  if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'g') {
    event.preventDefault()
    showGoToFixture.value = true
  }
}

const handleClickOutside = (event) => {
  const profileMenu = document.getElementById('profile-menu-container')
  if (profileMenu && !profileMenu.contains(event.target)) {
    showProfileMenu.value = false
  }
}

// Add event listener on mount and remove it on unmount
onMounted(async () => {
  themeStore.initTheme()
  window.addEventListener('keydown', handleKeyDown)
  document.addEventListener('click', handleClickOutside)
  
  // Initialize user store permissions when app loads
  await userStore.fetchAvailablePermissions()
  
  // Initialize SSE connection for real-time notifications
  if (userStore.isAuthenticated && userStore.currentUser.role == 'admin') {
    await notificationStore.initializeSSE()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
  document.removeEventListener('click', handleClickOutside)
  // Clean up split drag listeners
  document.removeEventListener('mousemove', handleSplitDragMove)
  document.removeEventListener('mouseup', handleSplitDragEnd)
  
  // Disconnect SSE when app unmounts
  notificationStore.disconnectSSE()
})

const showTicketsMenu = ref(false)
const showProfileTicketPopup = ref(false)

const showTemplatesMenu = ref(false)
const showTemplateManagement = ref(false)
const showApiManager = ref(false)
const showPlayersAliasManager = ref(false)
const showBoxscoresOverlay = ref(false)
const showSSETestPanel = ref(false)
const showBalancedLinesLadder = ref(false)
const showGlobalFixtureCalendar = ref(false)

// Debug panel
const showDebugPanel = ref(false)

// Split view functionality
const splitViewStore = useSplitViewStore()
const isSplitMode = computed(() => splitViewStore.isSplitMode)

// Database popup functionality
const databasePopupStore = useDatabasePopupStore()

// Database authentication state
const showDatabaseAuth = ref(false)
const databaseAuthModal = ref(null)
const isDatabaseAuthRequired = ref(true) // Set to false if no password protection is needed

// Check if database authentication is required
const checkDatabaseAuthRequired = async () => {
  try {
    // Check with your backend if database access requires authentication
    // For now, using a simple flag - you can make this an API call
    // const response = await axios.get('/api/database/auth-required')
    // return response.data.required
    
    // For demo purposes, return true to always require auth
    return isDatabaseAuthRequired.value
  } catch (error) {
    console.warn('Failed to check database auth requirements:', error)
    // Default to requiring auth for security
    return true
  }
}

// Open database popup with authentication check
const openDatabasePopup = async () => {
  try {
    const authRequired = await checkDatabaseAuthRequired()
    
    if (!authRequired) {
      // No authentication required, open popup directly
      databasePopupStore.addPopupTable('', 'data')
      return
    }

    // Check if already authenticated in this session
    const existingAuth = databaseAuthModal.value?.checkExistingAuth()
    if (existingAuth) {
      // Already authenticated, open popup directly
      databasePopupStore.addPopupTable('', 'data')
      return
    }

    // Show authentication modal
    showDatabaseAuth.value = true
  } catch (error) {
    console.error('Failed to open database popup:', error)
    // On error, show auth modal for security
    showDatabaseAuth.value = true
  }
}

// Handle successful database authentication
const handleDatabaseAuthenticated = (authData) => {
  showDatabaseAuth.value = false
  
  // Now open the database popup
  databasePopupStore.addPopupTable('', 'data')
  
  console.log('Database access granted:', authData)
}

// Handle database authentication cancellation
const handleDatabaseAuthCancel = () => {
  showDatabaseAuth.value = false
}

// Draggable split functionality
const splitPosition = ref(50) // Percentage for left panel
const isDraggingSplit = ref(false)

// Handle split drag start
const handleSplitDragStart = (event) => {
  isDraggingSplit.value = true
  
  // Prevent text selection during drag
  document.body.style.userSelect = 'none'
  document.body.style.webkitUserSelect = 'none'
  
  document.addEventListener('mousemove', handleSplitDragMove)
  document.addEventListener('mouseup', handleSplitDragEnd)
}

// Handle split drag move
const handleSplitDragMove = (event) => {
  if (!isDraggingSplit.value) return
  
  const mainElement = document.querySelector('main')
  if (!mainElement) return
  
  const rect = mainElement.getBoundingClientRect()
  const x = event.clientX - rect.left
  const percentage = Math.max(20, Math.min(80, (x / rect.width) * 100))
  
  splitPosition.value = percentage
}

// Handle split drag end
const handleSplitDragEnd = () => {
  isDraggingSplit.value = false
  
  // Restore text selection
  document.body.style.userSelect = ''
  document.body.style.webkitUserSelect = ''
  
  document.removeEventListener('mousemove', handleSplitDragMove)
  document.removeEventListener('mouseup', handleSplitDragEnd)
}

// Reset split position when exiting split mode
watch(isSplitMode, (newValue) => {
  if (!newValue) {
    splitPosition.value = 50
  }
})

// Market highlighting handlers
const handleMarketHighlight = (marketData) => {
  // Emit a global event that can be listened to by MarketCell components
  window.dispatchEvent(new CustomEvent('highlight-market', { 
    detail: marketData 
  }))
}

const handleClearHighlight = () => {
  // Emit a global event to clear highlights
  window.dispatchEvent(new CustomEvent('clear-highlight'))
}

// Note: Cmd+Shift+D keyboard shortcut is now handled in the database popup store
</script>

<template>
  <div class="min-h-screen bg-gray-50">

    <!-- Games Notification Header (Fixed at top) - admin only -->
    <GamesNotificationHeader v-if="currentUser.role === 'admin'" />

    <!-- <button @click="projectionsStore.globalSearchPopup = true"
                    class="fixed top-0 right-0 z-[1000] w-[50px] h-[50px] flex items-center justify-center bg-white border shadow-lg rounded-full text-gray-500 hover:text-gray-700 p-2 rounded-lg transition-colors"
                    title="Global Search">
              <i class="fas fa-search"></i>
            </button> -->
    <GlobalSearchPopup v-if="projectionsStore.globalSearchPopup" />
    <GoToFixturePopup v-if="showGoToFixture" @close="showGoToFixture = false" />
    
    <!-- Games Notification Overlay - admin only -->
    <GamesNotificationOverlay v-if="currentUser.role === 'admin'" />

    <!-- <WebSocketStatus v-if="userStore.currentUser && userStore.currentUser.role === 'admin'" /> -->
   
      <!-- <WebSocketStatus />
    <WebSocketUpdatesPanel 
      @highlight-market="handleMarketHighlight"
      @clear-highlight="handleClearHighlight"
    />  -->


    <!-- Show login page without any app layout -->
    <router-view v-if="route.name === 'login'"></router-view>

    <!-- Main app layout with sidebar -->
    <div v-else class="flex">
      <!-- Primary Navigation -->
      <aside 
        class="fixed left-0 top-0 flex h-full flex-col overflow-x-visible rounded-bl-lg rounded-br-lg bg-white shadow-lg transition-all duration-300 z-20"
        :class="isSidebarCollapsed ? 'w-16' : 'w-64'"
      >
        <!-- Logo -->
        <div class="flex flex-shrink-0 items-center p-4 border-b border-gray-200">
          <div class="flex items-center justify-center" :class="isSidebarCollapsed ? 'w-8' : 'w-auto'">
            <a href="/">
              <img src="/OC_print_logo_icon_color.svg" alt="OddsComposer Logo" class="h-8 w-auto">
            </a>
          </div>
          <span v-if="!isSidebarCollapsed" class="ml-3 font-bold text-xl text-gray-700">OddsComposer</span>
          <button 
            @click="isSidebarCollapsed = !isSidebarCollapsed"
            class="ml-auto text-gray-500 hover:text-gray-700"
          >
            <i :class="isSidebarCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
          </button>
        </div>

        <!-- Menu Items (scrolls; aside is not overflow-hidden so profile dropdown is visible) -->
        <nav class="min-h-0 flex-1 overflow-y-auto p-4">
          <ul class="space-y-2">
            <li v-for="item in menuItems" :key="item.id">
              <template v-if="item.hasSubmenu">
                <button 
                  @click="item.id === 'tutorials' ? handleTutorialClick(item) : toggleSubmenu(item)"
                  class="w-full flex items-center p-2 rounded-lg transition-colors"
                  :class="[
                    isActive(item.path) ? 'bg-pink-50 text-pink-500' : 'text-gray-700 hover:bg-gray-200',
                    isSidebarCollapsed ? 'justify-center' : ''
                  ]"
                >
                  <i :class="`fas ${item.icon} text-lg`"></i>
                  <template v-if="!isSidebarCollapsed">
                    <span class="ml-3">{{ item.label }}</span>
                    <i 
                      class="fas ml-auto"
                      :class="{
                        'fa-chevron-down transition-transform duration-300': item.id === 'games',
                        'fa-external-link-alt': item.id === 'tutorials',
                        'rotate-180': isGamesMenuOpen && item.id === 'games'
                      }"
                    ></i>
                  </template>
                </button>
                
                <!-- Keep submenu transition only for games -->
                <transition
                  v-if="item.id === 'games'"
                  enter-active-class="transition-all duration-300 ease-out"
                  leave-active-class="transition-all duration-300 ease-in"
                  enter-from-class="opacity-0 -translate-y-2"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-from-class="opacity-100 translate-y-0"
                  leave-to-class="opacity-0 -translate-y-2"
                >
                  <ul 
                    v-if="!isSidebarCollapsed && isGamesMenuOpen"
                    class="mt-2 ml-4 space-y-1 overflow-hidden"
                  >
                    <li v-for="subItem in item.subItems" :key="subItem.id">
                      <router-link 
                        :to="subItem.path"
                        class="flex items-center p-2 rounded-lg transition-colors"
                        :class="isActive(subItem.path) ? 'bg-pink-50 text-pink-500' : 'text-gray-700 hover:bg-gray-200'"
                      >
                        <i :class="`fas ${subItem.icon} text-lg`"></i>
                        <span class="ml-3">{{ subItem.label }}</span>
                      </router-link>
                    </li>
                  </ul>
                </transition>
              </template>

              <!-- Handle items with onClick separately from router-link items -->
              <template v-else>
                <template v-if="item.onClick">
                  <button 
                    @click="item.onClick"
                    class="w-full flex items-center p-2 rounded-lg transition-colors"
                    :class="[
                      'text-gray-700 hover:bg-gray-200',
                      isSidebarCollapsed ? 'justify-center' : ''
                    ]"
                  >
                    <i :class="`fas ${item.icon} text-lg`"></i>
                    <span v-if="!isSidebarCollapsed" class="ml-3">{{ item.label }}</span>
                  </button>
                </template>
                <template v-else>
                  <router-link 
                    :to="item.path"
                    class="flex items-center p-2 rounded-lg transition-colors"
                    :class="[
                      isActive(item.path) ? 'bg-pink-50 text-pink-500' : 'text-gray-700 hover:bg-gray-200',
                      isSidebarCollapsed ? 'justify-center' : ''
                    ]"
                  >
                    <i :class="`fas ${item.icon} text-lg`"></i>
                    <span v-if="!isSidebarCollapsed" class="ml-3">{{ item.label }}</span>
                  </router-link>
                </template>
              </template>
            </li>
            
            
          </ul>
        </nav>

        <!-- Add User Profile Section -->
        <div class="flex-shrink-0 border-t border-gray-200 bg-white">
            <!-- Notifications panel above search -->
             

            <button
              v-if="currentUser.role === 'admin'"
              @click="showGlobalFixtureCalendar = true"
              class="w-full flex flex-col items-center justify-center px-0 py-2 text-gray-700 hover:bg-gray-100 border-b border-gray-200 transition-colors relative"
              :class="isSidebarCollapsed ? '' : 'flex-row items-center px-4 py-2 gap-2'"
              title="Fixture Calendar"
            >
              <template v-if="isSidebarCollapsed">
                <i class="fas fa-calendar-alt text-xl mb-1"></i>
                <div class="flex items-center text-[8px] text-gray-400 leading-tight">
                  <span>CAL</span>
                </div>
              </template>
              <template v-else>
                <i class="fas fa-calendar-alt text-sm"></i>
                <span class="ml-2 font-medium text-sm">Fixture Calendar</span>
              </template>
            </button>
            <button
              v-if="currentUser.role === 'admin'"
              @click="showApiManager = true"
              class="w-full flex flex-col items-center justify-center px-0 py-2 text-gray-700 hover:bg-gray-100 border-b border-gray-200 transition-colors relative"
              :class="isSidebarCollapsed ? '' : 'flex-row items-center px-4 py-2 gap-2'"
              title="API Manager" 
            >
              <template v-if="isSidebarCollapsed">
                <i class="fas fa-code text-xl mb-1"></i>
                <div class="flex items-center text-[8px] text-gray-400 leading-tight">
                  <span>API</span>
                </div>
              </template>
              <template v-else>
                <i class="fas fa-code text-sm"></i>
                <span class="ml-2 font-medium text-sm">API Manager</span>
              </template>
            </button>
            <div 
              v-if="currentUser.role === 'admin'" 
              class="relative group"
            >
              <AutomatedProjectionsProfiles :isSidebarCollapsed="isSidebarCollapsed" />
              <AutoV2 :isSidebarCollapsed="isSidebarCollapsed" />
           
            </div>
            <SidebarNotificationsPanel 
              v-if="currentUser.role === 'admin'"
              :collapsed="isSidebarCollapsed" :unread="unreadCount" @open-notifications="showNotifications = false" />
            <button
            v-if="currentUser.role === 'admin'"
              @click="projectionsStore.globalSearchPopup = true"
              class="hidden w-full flex flex-col items-center justify-center px-0 py-2 text-gray-700 hover:bg-gray-100 border-b border-gray-200 transition-colors relative"
              :class="isSidebarCollapsed ? '' : 'flex-row items-center px-4 py-2 gap-2'"
              title="Global Search"
            >
              <template v-if="isSidebarCollapsed">
                <i class="fas fa-search text-xl mb-1"></i>
                <div class="flex items-center text-[8px] text-gray-400 leading-tight">
                  <span>CMD</span>
                  <span>+</span>
                  <span>SHIFT</span>
                  <span>+</span>
                  <span>S/F</span>
                </div>
              </template>
              <template v-else>
                <i class="fas fa-search text-sm"></i>
                <span class="ml-2 font-medium text-sm">Global Search</span>
                <span class="ml-auto text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">CMD + SHIFT + S/F</span>
              </template>
            </button>
            
            <!-- SSE Test Panel Button -->
            <button
            v-if="currentUser.role === 'admin'"
              @click="showSSETestPanel = true"
              class="hidden w-full flex flex-col items-center justify-center px-0 py-2 text-gray-700 hover:bg-gray-100 border-b border-gray-200 transition-colors relative"
              :class="isSidebarCollapsed ? '' : 'flex-row items-center px-4 py-2 gap-2'"
              title="SSE Test Panel"
            >
              <template v-if="isSidebarCollapsed">
                <i class="fas fa-broadcast-tower text-xl mb-1"></i>
                <div class="flex items-center text-[8px] text-gray-400 leading-tight">
                  <span>SSE</span>
                  <span>TEST</span>
                </div>
              </template>
              <template v-else>
                <i class="fas fa-broadcast-tower text-sm"></i>
                <span class="ml-2 font-medium text-sm">SSE Test</span>
                <div 
                  class="ml-auto w-2 h-2 rounded-full"
                  :class="sseConnected ? 'bg-green-500' : 'bg-red-500'"
                  :title="sseConnected ? 'Connected' : 'Disconnected'"
                ></div>
              </template>
            </button>
          <!-- Chat Button -->
          <!-- <ChatButton /> -->

          <!-- Notifications -->
          <!-- <button
            @click="showNotifications = !showNotifications"
            class="w-full flex items-center p-4 text-gray-700 hover:bg-gray-200 border-b border-gray-200 relative"
            :class="[isSidebarCollapsed ? 'justify-center' : '']"
          >
            <i class="fas fa-bell text-lg"></i>
            <span v-if="!isSidebarCollapsed" class="ml-3">Notifications</span>
            <div 
              v-if="unreadCount > 0"
              class="absolute w-5 h-5 bg-pink-500 text-white rounded-full text-xs flex items-center justify-center"
              :class="[isSidebarCollapsed ? 'top-2 right-2' : 'top-3 right-4']"
            >
              {{ unreadCount }}
            </div>
          </button> -->

          <!-- Notifications Dropdown -->
          <div 
            
            v-if="showNotifications && !isSidebarCollapsed"
            class="absolute bottom-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg rounded-t-lg overflow-hidden"
          >
            <!-- Reuse NotificationBar content -->
            <div class="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 class="font-medium">Notifications</h3>
              <button 
                @click="markAllAsRead"
                class="text-sm text-pink-500 hover:text-pink-600"
              >
                Mark all as read
              </button>
            </div>

            <div class="max-h-96 overflow-y-auto">
              <div 
                v-for="notification in notifications" 
                :key="notification.id"
                class="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                :class="{ 'bg-gray-50': notification.read }"
                @click="markAsRead(notification)"
              >
                <div class="flex items-start gap-3">
                  <div 
                    class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    :class="{
                      'bg-blue-100 text-blue-500': notification.type === 'info',
                      'bg-green-100 text-green-500': notification.type === 'success',
                      'bg-yellow-100 text-yellow-500': notification.type === 'warning',
                      'bg-red-100 text-red-500': notification.type === 'error'
                    }"
                  >
                    <i 
                      class="fas"
                      :class="{
                        'fa-info': notification.type === 'info',
                        'fa-check': notification.type === 'success',
                        'fa-exclamation': notification.type === 'warning',
                        'fa-times': notification.type === 'error'
                      }"
                    ></i>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm">{{ notification.message }}</p>
                    <p class="text-xs text-gray-500 mt-1">{{ notification.time }}</p>
                  </div>
                  <div 
                    v-if="!notification.read"
                    class="w-2 h-2 rounded-full bg-pink-500"
                  ></div>
                </div>
              </div>
            </div>

            <div class="p-4 border-t border-gray-200">
              <router-link 
                to="/notifications"
                class="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center"
              >
                View all notifications
                <i class="fas fa-chevron-right ml-2"></i>
              </router-link>
            </div>
          </div>

          <!-- Theme Toggle -->
          <!-- <button
            @click="toggleTheme"
            class="w-full flex items-center p-4 text-gray-700 hover:bg-gray-200 border-b border-gray-200"
            :class="[isSidebarCollapsed ? 'justify-center' : '']"
          >
            <i :class="['fas', getThemeIcon, 'text-lg']"></i>
            <span v-if="!isSidebarCollapsed" class="ml-3">
              {{ themeStore.theme.charAt(0).toUpperCase() + themeStore.theme.slice(1) }} Mode
            </span>
          </button> -->

          <!-- User Profile -->
          <div 
            id="profile-menu-container" 
            class="relative z-30"
          >
            <button
              @click="showProfileMenu = !showProfileMenu"
              class="w-full flex items-center p-4 hover:bg-gray-200"
              :class="[isSidebarCollapsed ? 'justify-center' : '']"
            >
              <!-- User Avatar/Initials -->
              <div 
                class="w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center flex-shrink-0"
              >
                <img 
                  v-if="currentUser.avatar" 
                  :src="currentUser.avatar" 
                  :alt="currentUser.name"
                  class="w-full h-full rounded-full"
                >
                <span v-else class="text-sm font-medium">{{ userInitials }}</span>
              </div>

              <!-- User Info (only show when sidebar is expanded) -->
              <div v-if="!isSidebarCollapsed" class="ml-3 text-left flex-1 min-w-0">
                <div class="font-medium truncate">{{ currentUser.name }}</div>
                <div class="flex items-center gap-2">
                  <span :class="{
                    'px-2 py-1 text-xs rounded-full': true,
                    'bg-yellow-100 text-yellow-800': currentUser.role === 'admin',
                    'bg-green-100 text-green-800': currentUser.role === 'trader',
                    'bg-blue-100 text-blue-800': currentUser.role === 'client'
                  }">
                    {{ currentUser.role }}
                  </span>
                </div>
              </div>

              <!-- Status Indicator -->
              <div 
                class="w-2.5 h-2.5 rounded-full bg-green-500"
                :class="[isSidebarCollapsed ? 'right-3 top-4' : 'right-4 top-6']"
              ></div>
            </button>

            <!-- Profile Dropdown -->
            <div 
              v-if="showProfileMenu" 
              class="absolute bg-gray-100 border border-gray-200 shadow-lg rounded-lg overflow-hidden"
              :class="[
                isSidebarCollapsed 
                  ? 'left-full bottom-0 ml-2' 
                  : 'bottom-full left-0 right-0 mb-2'
              ]"
              style="min-width: 240px;"
            >
              <!-- Header with user info -->
              <div class="p-4 bg-gray-100 border-b border-gray-200 text-left">
                <div class="flex items-center gap-3">
                  <div class="w-12 h-12 rounded-full bg-pink-500 text-white flex items-center justify-center flex-shrink-0">
                    <img 
                      v-if="currentUser.avatar" 
                      :src="currentUser.avatar" 
                      :alt="currentUser.name"
                      class="w-full h-full rounded-full object-cover"
                    >
                    <span v-else class="text-lg font-medium">{{ userInitials }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-lg text-gray-700 truncate">{{ currentUser.name }}</div>
                    <div class="text-sm text-gray-500 truncate">{{ currentUser.email }}</div>
                  </div>
                </div>
                <div class="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <div class="flex items-center gap-1.5">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>Online</span>
                  </div>
                  <span class="text-gray-400">•</span>
                  <span>{{ currentUser.role }}</span>
                </div>
              </div>
              
              <!-- Menu Items -->
              <div class="py-2 bg-white text-left">
                <router-link 
                  to="/profile"
                  @click="showProfileMenu = false"
                  class="flex items-center justify-start px-4 py-2.5 text-gray-700 hover:bg-gray-200 transition-colors text-left"
                >
                  <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <i class="fas fa-user-circle text-gray-600"></i>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <div class="font-medium">Profile</div>
                    <div class="text-xs text-gray-500">Manage your account</div>
                  </div>
                </router-link>
                
                <router-link 
                  to="/settings"
                  @click="showProfileMenu = false"
                  class="flex items-center justify-start px-4 py-2.5 text-gray-700 hover:bg-gray-200 transition-colors text-left"
                >
                  <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <i class="fas fa-cog text-gray-600"></i>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <div class="font-medium">Settings</div>
                    <div class="text-xs text-gray-500">Team management</div>
                  </div>
                </router-link>

                <button 
                  @click="showProfileMenu = false; showProfileTicketPopup = true"
                  class="w-full flex items-center justify-start px-4 py-2.5 text-gray-700 hover:bg-gray-200 transition-colors text-left"
                >
                  <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <i class="fas fa-ticket-alt text-gray-600"></i>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <div class="font-medium">Ticket</div>
                    <div class="text-xs text-gray-500">View and manage tickets</div>
                  </div>
                </button>

                <div class="h-px bg-gray-200 my-2"></div>

                <button 
                  @click="logout"
                  class="w-full flex items-center justify-start px-4 py-2.5 text-gray-700 hover:bg-gray-200 transition-colors text-left"
                >
                  <div class="w-9 h-9 rounded-full bg-pink-50 flex items-center justify-center mr-3 flex-shrink-0">
                    <i class="fas fa-sign-out-alt text-pink-500"></i>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <div class="font-medium text-pink-500">Logout</div>
                    <div class="text-xs text-gray-500">Sign out of your account</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- Replace the old third menu with the new component -->
      <ThirdMenu 
        :show-secondary-menu="showSecondaryMenu"
        :is-sidebar-collapsed="isSidebarCollapsed"
      />

      <aside 
        class="fixed h-full bg-white shadow-lg overflow-y-auto z-10 transition-transform duration-300"
        :class="[
          showSecondaryMenu ? 'translate-x-0' : '-translate-x-full',
          isSidebarCollapsed ? 'left-16 w-64' : 'left-64 w-72'
        ]"
      >
        <SecondaryMenu v-if="showSecondaryMenu" />
      </aside>

      <!-- Main Content -->
      <main 
        class="transition-all duration-300 min-h-screen bg-gray-50 relative"
        :class="[
          isSidebarCollapsed ? 'pl-16' : 'pl-64',
          showSecondaryMenu ? (isSidebarCollapsed ? 'pl-80' : 'pl-[34rem]') : '',
          isSplitMode ? 'flex' : 'w-full',
          hasNewGames ? 'pt-16' : 'pt-0'
        ]"
      >
        <!-- Top-right SSE Status Indicator (simplified) -->
        <div v-if="currentUser.role === 'admin'" class="hidden fixed top-4 right-4 z-30">
          <!-- SSE Connection Status Indicator -->
          <div 
            class="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
            :class="sseConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
            :title="sseConnected ? 'Real-time notifications connected' : 'Real-time notifications disconnected'"
          >
            <div 
              class="w-2 h-2 rounded-full"
              :class="sseConnected ? 'bg-green-500' : 'bg-red-500'"
            ></div>
            <span class="hidden sm:inline">{{ sseConnected ? 'Live' : 'Offline' }}</span>
          </div>
        </div>

        <!-- Global Database Popup Button -->
        <!-- <div class="fixed top-4 right-20 z-30">
          <div class="flex items-center space-x-2"> -->
            <!-- Authentication Status Indicator -->
            <!-- <div 
              v-if="isDatabaseAuthRequired"
              class="flex items-center bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
              title="Database access requires authentication"
            >
              <i class="fas fa-shield-alt mr-1"></i>
              Auth Required
            </div>
             -->
            <!-- Database Popup Button -->
            <!-- <button
              @click="openDatabasePopup"
              class="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-lg transition-colors relative group"
              :title="isDatabaseAuthRequired ? 'Open Database Popup (Authentication Required)' : 'Open Database Popup (Ctrl+Shift+D)'"
            >
              <i class="fas fa-database text-sm"></i> -->
              
              <!-- Security Badge -->
              <!-- <div 
                v-if="isDatabaseAuthRequired"
                class="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border border-white"
                title="Secured with password"
              ></div> -->
              
              <!-- Tooltip -->
              <!-- <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {{ isDatabaseAuthRequired ? 'Requires Authentication' : 'Ctrl+Shift+D' }}
              </div> -->
            <!-- </button> -->
            
            <!-- Toggle Auth Requirement (Dev/Admin Feature) -->
            <!-- <button
              v-if="currentUser.role === 'admin'"
              @click="isDatabaseAuthRequired = !isDatabaseAuthRequired"
              class="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-full shadow-lg transition-colors text-xs"
              :title="isDatabaseAuthRequired ? 'Disable Database Authentication' : 'Enable Database Authentication'"
            >
              <i :class="isDatabaseAuthRequired ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
            </button> -->
          <!-- </div>
        </div> -->



        <!-- Main Content Area -->
        <div 
          class="transition-all duration-200 relative"
        :class="[
          isSplitMode ? 'border-r border-gray-300' : 'w-full',
          // Add top padding when games notification header is visible
          'pt-0'
        ]"
          :style="isSplitMode ? { width: `${splitPosition}%` } : {}"
        >
          <router-view></router-view>
        </div>

        <!-- Draggable Divider -->
        <div 
          v-if="isSplitMode"
          class="relative bg-gray-300 hover:bg-blue-400 transition-colors duration-200 cursor-col-resize flex items-center justify-center group"
          :class="{
            'bg-blue-500': isDraggingSplit
          }"
          style="width: 4px; min-width: 4px;"
          @mousedown="handleSplitDragStart"
        >
          <!-- Drag Handle Visual Indicator -->
          <div class="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center">
            <div class="w-1 h-8 bg-gray-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                 :class="{ 'opacity-100 bg-blue-300': isDraggingSplit }">
            </div>
          </div>
          
          <!-- Tooltip -->
          <div class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
            Drag to resize
          </div>
        </div>

        <!-- Split View Panel -->
        <div 
          v-if="isSplitMode"
          id="split-panel"
          class="bg-white min-h-screen relative overflow-hidden"
          :style="{ width: `${100 - splitPosition}%` }"
        >
          <!-- Split Panel Header -->
          <div class="h-16 bg-gray-100 border-b border-gray-200 flex items-center justify-between px-4">
            <h3 class="text-lg font-semibold text-gray-800">Split View</h3>
            <div class="flex items-center gap-2">
              <!-- Split Position Indicator -->
              <span class="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                {{ Math.round(splitPosition) }}% | {{ Math.round(100 - splitPosition) }}%
              </span>
              <button
                @click="splitViewStore.disableSplitMode()"
                class="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-200 transition-colors"
                title="Exit Split View"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <!-- Split Panel Content -->
          <div class="h-[calc(100vh-4rem)] overflow-auto">
            <!-- Content will be teleported here -->
          </div>
        </div>
      </main>

      <GlobalContextMenu v-if="userStore.currentUser?.role === 'admin'"/>
      
      <NotificationOverlay 
        :show="showNotifications"
        @close="showNotifications = false"
      />

      <!-- Toast Notifications -->
      <NotificationToast />

      <!-- Re-login popup when the API token has expired -->
      <SessionExpiredModal />

      <TutorialOverlay 
        :show="showTutorials"
        @close="showTutorials = false"
      />

      <UsersMenuPopup
        :show="showUsersMenu"
        @close="showUsersMenu = false"
      />

      <ProfileTicketPopup
        :show="showProfileTicketPopup"
        @close="showProfileTicketPopup = false"
      />

      <TicketsMenuPopup
        :show="showTicketsMenu"
        @close="showTicketsMenu = false"
      />

      <WorkoutDiaryOverlay
        :show="showWorkoutDiary"
        @close="showWorkoutDiary = false"
      />

      <ServerManagerOverlay
        :show="showServerManager"
        @close="showServerManager = false"
      />

      <TemplatesMenuPopup
        :show="showTemplatesMenu"
        @close="showTemplatesMenu = false"
      />

      <BalancedLinesLadderPanel
        :show="showBalancedLinesLadder"
        @close="showBalancedLinesLadder = false"
      />

      <!-- Template Management Popup -->
      <TemplateManagementPopup
        :show="showTemplateManagement"
        @close="showTemplateManagement = false"
      />

      <!-- Global Database Popup Windows -->
      <DatabasePopupWindows v-if="userStore.currentUser?.role === 'admin'" />

      <!-- Database Authentication Modal -->
      <DatabaseAuthModal
        ref="databaseAuthModal"
        :show="showDatabaseAuth"
        @authenticated="handleDatabaseAuthenticated"
        @cancel="handleDatabaseAuthCancel"
      />

      <!-- Debug Panel -->
      <AuthDebugger
        v-if="showDebugPanel"
        @close="showDebugPanel = false"
      />

      <!-- API Manager Popup -->
      <ApiManagerPopup
        :show="showApiManager"
        @close="showApiManager = false"
      />

      <!-- Global Fixture Calendar -->
      <GlobalFixtureCalendarModal
        :visible="showGlobalFixtureCalendar"
        @close="showGlobalFixtureCalendar = false"
      />

      <!-- Player Alias Manager -->
      <PlayerAliasManager
        :visible="showPlayersAliasManager"
        @close="showPlayersAliasManager = false"
      />

      <!-- Boxscores Overlay -->
      <BoxscoresOverlay
        :isOpen="showBoxscoresOverlay"
        @close="showBoxscoresOverlay = false"
      />

      <!-- SSE Test Panel Overlay -->
      <div 
        v-if="showSSETestPanel"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        @click.self="showSSETestPanel = false"
      >
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div class="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-800">SSE Test Panel</h2>
            <button 
              @click="showSSETestPanel = false"
              class="text-gray-400 hover:text-gray-600"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="p-4">
            <SSETestPanel />
          </div>
        </div>
      </div>

      <!-- Error Overlay -->
      <!-- <ErrorOverlay v-if="userStore.currentUser?.id === 6" /> -->

    </div>
  </div>
</template>

<style>
/* Reset some default styles */
body {
  margin: 0;
  min-height: 100vh;
  @apply bg-gray-50;
}

#app {
  min-height: 100vh;
  max-width: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

main {
  max-width: none !important;
}

/* Font Awesome icons styles */
.fas {
  @apply inline-block;
}

/* Hide scrollbar for Chrome, Safari and Opera */
::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
* {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Smooth transitions */
.translate-x-0 {
  transform: translateX(0);
}

.-translate-x-full {
  transform: translateX(-100%);
}

/* Add these transition styles */
.transition-all {
  transition-property: all;
}

.duration-300 {
  transition-duration: 300ms;
}

.ease-in {
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.ease-out {
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}

.translate-y-0 {
  transform: translateY(0);
}

.-translate-y-2 {
  transform: translateY(-0.5rem);
}

/* Add to existing styles */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
